/**
 * Waitlist Status Page
 * "Share your link to move up 10 spots for each friend who joins"
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Copy, Check, Share2, Clock, Users, TrendingUp, Crown, Mail, ArrowRight, Twitter, Facebook, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getWaitlistReferralLink } from '../config/app-config';
import { getSharePopupFeatures } from '../utils/popup';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function WaitlistStatus() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [position, setPosition] = useState(0);
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState(0);
  const [totalWaitlist, setTotalWaitlist] = useState(0);
  const [email, setEmail] = useState('');

  useEffect(() => {
    loadWaitlistData();
  }, []);

  const loadWaitlistData = async () => {
    const code = typeof localStorage !== 'undefined' ? localStorage.getItem('waitlist_referral_code') : null;
    if (!code) {
      return;
    }

    setReferralCode(code);

    try {
      const { getWaitlistStatusByReferralCode } = await import('../lib/db-storage');
      const data = await getWaitlistStatusByReferralCode(code);
      setEmail(data.email ?? '');
      setReferrals(data.referrals ?? 0);
      setPosition(data.position ?? 0);
      setTotalWaitlist(data.totalWaitlist ?? 0);
    } catch (_) {
      // Fallback: keep 0 position if API fails
    }
  };

  const getReferralLinkUrl = () => {
    return getWaitlistReferralLink(referralCode);
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(getReferralLinkUrl());
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToSocial = (platform: 'twitter' | 'facebook' | 'whatsapp') => {
    const text = `I'm #${position} on the waitlist for TENSION - the AI app that helps you understand relationship tension. Join me!`;
    const url = getReferralLinkUrl();

    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
    };

    window.open(urls[platform], '_blank', getSharePopupFeatures());
  };

  const percentile = totalWaitlist > 0 ? Math.round((1 - position / totalWaitlist) * 100) : 0;
  const spotsToNextTier = position > 100 ? position - 100 : position > 50 ? position - 50 : position > 10 ? position - 10 : position - 1;

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b border-neutral-200/80 bg-white/90 px-6 py-4 backdrop-blur">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">TENSION Waitlist</h1>
            <p className="text-sm text-neutral-600">Your exclusive early access status</p>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/early-access')}
            size="sm"
          >
            ← Back to Landing
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 pb-4 pt-6">
        <StockHeroStrip stockKey="coupleHomeRelax" alt="Illustrative stock imagery" />
      </div>

      <div className="mx-auto max-w-4xl space-y-8 px-6 py-8">
        {/* Position Card */}
        <Card className="p-8 bg-gradient-to-br from-purple-600 to-pink-600 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-semibold">Early Access Position</span>
              </div>
              
              <div className="mb-2">
                <div className="text-7xl font-bold mb-2">#{position}</div>
                <div className="text-lg text-purple-100">
                  out of {totalWaitlist.toLocaleString()} on the waitlist
                </div>
              </div>

              <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm">
                  Top {percentile}% • {referrals} referrals
                </span>
              </div>
            </div>

            {position <= 100 && (
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-sm font-semibold mb-1">
                  🎉 You're in the priority queue!
                </div>
                <div className="text-xs text-purple-100">
                  You'll be among the first to receive a beta key
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-4 text-center">
            <div className="text-3xl font-bold text-purple-600 mb-1">{referrals}</div>
            <div className="text-xs text-neutral-600">Referrals</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-3xl font-bold text-green-600 mb-1">{referrals * 10}</div>
            <div className="text-xs text-neutral-600">Spots Earned</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-600 mb-1">{spotsToNextTier}</div>
            <div className="text-xs text-neutral-600">
              {position > 100 ? 'To Top 100' : position > 50 ? 'To Top 50' : position > 10 ? 'To Top 10' : 'To #1'}
            </div>
          </Card>
        </div>

        {/* How to Move Up */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
          <h3 className="text-lg font-bold text-neutral-900 mb-4">
            🚀 Move Up Faster
          </h3>
          <div className="space-y-3 text-sm text-neutral-700">
            <p>
              <strong>Share your unique referral link.</strong> For every friend who joins the waitlist using your link, you move up <strong>10 spots</strong>!
            </p>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 text-xs text-neutral-600 mb-2">
                <span>📈</span>
                <span>Example: 5 referrals = 50 spots up = Jump from #500 to #450</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-neutral-600">
                <span>👑</span>
                <span>Top 100 users get beta keys first!</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Share Your Link */}
        <Card className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Share Your Referral Link</h3>
            
            {/* Copy Link */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-neutral-700">Your Unique Link</label>
              <div className="flex gap-2">
                <Input
                  value={getReferralLinkUrl()}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  onClick={copyReferralLink}
                  className="gap-2 whitespace-nowrap"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>

          {/* Social Sharing */}
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-3 block">Share on Social</label>
            <div className="grid grid-cols-3 gap-3">
              <Button
                variant="outline"
                className="flex-col h-auto py-4 gap-2"
                onClick={() => shareToSocial('twitter')}
              >
                <Twitter className="w-5 h-5" />
                <span className="text-xs">Twitter</span>
              </Button>
              <Button
                variant="outline"
                className="flex-col h-auto py-4 gap-2"
                onClick={() => shareToSocial('facebook')}
              >
                <Facebook className="w-5 h-5" />
                <span className="text-xs">Facebook</span>
              </Button>
              <Button
                variant="outline"
                className="flex-col h-auto py-4 gap-2"
                onClick={() => shareToSocial('whatsapp')}
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-xs">WhatsApp</span>
              </Button>
            </div>
          </div>
        </Card>

        {/* Tiers Info */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Access Tiers</h3>
          <div className="space-y-4">
            <div className="tension-glow flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Crown className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-amber-900">Top 10 (VIP)</div>
                <div className="text-sm text-amber-700">
                  Immediate early access + lifetime Pro discount + exclusive swag
                </div>
              </div>
            </div>

            <div className="tension-glow flex items-start gap-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <Users className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-purple-900">Top 50 (Early Bird)</div>
                <div className="text-sm text-purple-700">
                  Early access in first wave + 50% off Premium for life
                </div>
              </div>
            </div>

            <div className="tension-glow flex items-start gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-blue-900">Top 100 (Priority)</div>
                <div className="text-sm text-blue-700">
                  Early access in second wave + 30% off Premium
                </div>
              </div>
            </div>

            <div className="tension-glow flex items-start gap-4 p-4 bg-neutral-50 border border-neutral-200 rounded-lg">
              <Mail className="w-6 h-6 text-neutral-600 flex-shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-neutral-900">Everyone Else</div>
                <div className="text-sm text-neutral-600">
                  Rolling early access as capacity increases
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* CTA */}
        <Card className="p-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-center">
          <h3 className="text-2xl font-bold mb-2">
            The More You Share, The Sooner You Get In
          </h3>
          <p className="text-purple-100 mb-6">
            Every referral moves you 10 spots closer to early access
          </p>
          <Button
            size="lg"
            className="bg-white text-purple-600 hover:bg-purple-50"
            onClick={copyReferralLink}
          >
            <Share2 className="w-5 h-5 mr-2" />
            Copy & Share Link
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Card>
      </div>
    </div>
    </WarmMarketingBackdrop>
  );
}